// 3rd part libs
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { X } from 'icons/indexed-icons/X';
import { default as theme } from 'themes';
// Custom hooks
import { useKeypress } from '../../hooks';
import { ModalSize, ModalSizeType } from 'types';

interface ModalProps {
  title?: string | JSX.Element;
  visible: boolean;
  children: React.ReactNode;
  overrideStyles?: boolean;
  size?: ModalSizeType;
  showCloseButton?: boolean;
  onClose: () => void;
  allowContentScroll?: boolean;
  hasDropdowns?: boolean;
  maxWidth?: string;
  contentBorderRadius?: string;
  showScrollBar?: boolean;
  widthOverride?: string;
  heightOverride?: string;
  bgOpacity?: string;
  showVanityBorder?: boolean;
  vanityBorderColor?: string;
  hasBackgroundColor?: boolean;
  backgroundColor?: string;
  customCloseButtonColor?: string;
  isBackdropClose?: boolean;
  zIndex?: number;
  isLandscape?: boolean;
  closeBtnPos?: {
    top?: string;
    right?: string;
  };
}

interface ContentProps {
  allowContentScroll?: boolean;
  visible: boolean;
  size?: ModalSizeType;
  hasDropdowns?: boolean;
  maxWidth?: string;
  contentBorderRadius?: string;
  widthOverride?: string;
  heightOverride?: string;
  showVanityBorder?: boolean;
  vanityBorderColor?: string;
  hasBackgroundColor?: boolean;
  backgroundColor?: string;
  isDeviceLandscape?: boolean;
}

interface BackdropProps {
  visible: boolean;
  bgOpacity?: string;
  zIndex?: number;
}

const fadeInAnim = keyframes`
 0% { opacity: 0 }
 100% { opacity: 1 }
`;

const fadeOutAnim = keyframes`
 0% { opacity: 1 }
 100% { opacity: 0 }
`;

// Slide-in animations
const slideInAnim = keyframes`
  0% { 
    opacity: 0;
    transform: translateY(100%);
  }
  100% { 
    opacity: 1;
    transform: translateY(0%);
  }
`;

const slideInLandscapeAnim = keyframes`
  0% { 
    opacity: 0;
    transform: translate(-50%, -50%) rotate(90deg) scale(0.9);
  }
  100% { 
    opacity: 1;
    transform: translate(-50%, -50%) rotate(90deg) scale(1);
  }
`;

// Slide-out animations
const slideOutAnim = keyframes`
  0% { 
    opacity: 1;
    transform: translateY(0%);
  }
  100% { 
    opacity: 0;
    transform: translateY(100%);
  }
`;

const slideOutLandscapeAnim = keyframes`
  0% { 
    opacity: 1;
    transform: translate(-50%, -50%) rotate(90deg) scale(1);
  }
  100% { 
    opacity: 0;
    transform: translate(-50%, -50%) rotate(90deg) scale(0.9);
  }
`;

const Backdrop = styled.div<BackdropProps>`
  z-index: ${({ zIndex }) => zIndex || 998};
  background: rgba(0, 0, 0, ${({ bgOpacity }) => (bgOpacity ? bgOpacity : '0.3')});
  position: fixed;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  animation: ${({ visible }) => (visible ? fadeInAnim : fadeOutAnim)} 0.2s forwards ease-out;
`;

const Content = styled.div<ContentProps & { isLandscape?: boolean }>`
  overflow-y: ${({ hasDropdowns }) => (hasDropdowns ? 'visible' : 'auto')};
  z-index: 99999;
  max-height: 90vh;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  margin: 1.8125rem 2.1875rem 0.0625rem;
  display: flex;
  flex-direction: column;
  margin: auto;
  max-width: ${({ maxWidth }) => maxWidth || 'none'};
  border-radius: 0.625rem;
  border-bottom: ${({ showVanityBorder, vanityBorderColor }) =>
    showVanityBorder && `${'6px solid'} ${vanityBorderColor}`};
  > div {
    border-radius: ${({ contentBorderRadius }) => contentBorderRadius};
    overflow: ${({ allowContentScroll }) => !allowContentScroll && 'visible'};
  }
  width: ${({ size, widthOverride }) =>
    widthOverride
      ? widthOverride
      : size === ModalSize.XXSMALL || size === ModalSize.XXSMALL_AUTO_HEIGHT
      ? '17.75rem'
      : size === ModalSize.XSMALL || size === ModalSize.XSMALL_AUTO_HEIGHT
      ? '23.4375rem'
      : size === ModalSize.SMALL || size === ModalSize.SMALL_AUTO_HEIGHT
      ? '60%'
      : size === ModalSize.MEDIUM
      ? '70%'
      : '90%'};
  height: ${({ size, heightOverride }) =>
    heightOverride
      ? heightOverride
      : size === ModalSize.XXSMALL
      ? '10rem'
      : size === ModalSize.XSMALL
      ? '12rem'
      : size === ModalSize.SM
      ? '568px'
      : size === ModalSize.SMALL
      ? '70%'
      : size === ModalSize.MEDIUM
      ? '80%'
      : size === ModalSize.XSMALL_AUTO_HEIGHT ||
        size === ModalSize.SMALL_AUTO_HEIGHT ||
        size == ModalSize.XXSMALL_AUTO_HEIGHT
      ? 'fit-content'
      : '90%'};
  background: ${({ theme, backgroundColor, hasBackgroundColor }) =>
    hasBackgroundColor ? backgroundColor : theme.colors.background.background1};
  animation: ${({ isLandscape, visible }) =>
      visible
        ? isLandscape
          ? slideInLandscapeAnim
          : slideInAnim
        : isLandscape
        ? slideOutLandscapeAnim
        : slideOutAnim}
    0.2s forwards ease-out;

  ${({ isDeviceLandscape }) =>
    isDeviceLandscape &&
    `padding: 0 10px; 
    .modal-inner-content {
      height: auto;
      display: flex;
      flex-direction: column;
      flex: 1;
    }`}

  /* Landscape mode positioning and rotation */
  ${({ isLandscape, size, heightOverride }) =>
    isLandscape
      ? `
      position: fixed;
      top: 50%;
      left: 50%;
      width: ${size == ModalSize.SMALL_AUTO_HEIGHT ? 'inherit' : '90vh !important'};
      height: ${
        size == ModalSize.SMALL_AUTO_HEIGHT ? (heightOverride ? heightOverride : 'inherit') : '90vw'
      };
      transform: translate(-50%, -50%) rotate(90deg);
      transform-origin: center center;
      margin: 0;
      max-width: none;
      padding: 0 20px;

      `
      : ` `};
  padding: 0;
`;

const ModalHeaderContainer = styled.div<{ isLandscape?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: ${({ isLandscape }) => (isLandscape ? '1rem' : '0')};
  position: ${({ isLandscape }) => (isLandscape ? 'relative' : 'static')};
`;

const ModalTitleContainer = styled.div<{ isLandscape?: boolean }>`
  display: flex;
  flex-direction: row;
  color: ${(props) => props.theme.colors.darkGrey};
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 1.3125rem;
  letter-spacing: 0rem;
  padding: 1.5rem 1rem 1.6rem;
  text-align: left;
  width: 100%;
`;

const CloseButton = styled.button<{ isLandscape?: boolean; top?: string; right?: string }>`
  background: inherit;
  border: none;
  cursor: pointer;
  font-size: 1.6rem;
  position: absolute;
  top: ${({ isLandscape, top }) => (isLandscape ? '1rem' : top || '1rem')};
  right: ${({ isLandscape, right }) => (isLandscape ? '1rem' : right || '0.5rem')};
  z-index: 20;
`;

interface ModalBodyProps {
  allowContentScroll?: boolean;
  overrideStyles: boolean;
  showScrollBar?: boolean;
  isDeviceLandscape?: boolean;
}

const ModalBodyContainer = styled.div<ModalBodyProps & { isLandscape?: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${(props) => (props.overrideStyles ? '0 4.0625rem 1.5rem 3rem' : '0')};
  overflow: auto;
  overflow-y: ${({ allowContentScroll }) => (allowContentScroll ? 'scroll' : 'auto')};
  height: 'auto';
  width: 100%;

  /* Hide scrollbar */
  scrollbar-width: ${({ showScrollBar }) => !showScrollBar && 'none'};
  &::-webkit-scrollbar {
    display: ${({ showScrollBar }) => !showScrollBar && 'none'};
  }
  -ms-overflow-style: none;
  .date-range-picker {
    border-radius: 0rem !important;
  }
`;

const Modal = ({
  title,
  visible,
  children,
  onClose,
  overrideStyles = false,
  size = ModalSize.LARGE,
  showCloseButton = true,
  allowContentScroll = false,
  hasDropdowns,
  maxWidth,
  contentBorderRadius = '0.625rem',
  showScrollBar = false,
  widthOverride,
  heightOverride,
  bgOpacity,
  showVanityBorder = false,
  vanityBorderColor,
  hasBackgroundColor = false,
  backgroundColor,
  isBackdropClose = true,
  zIndex,
  closeBtnPos,
  isLandscape = false
}: ModalProps): JSX.Element | null => {
  const [mounted, setMounted] = useState(false);
  const [isDeviceLandscape, setIsDeviceLandscape] = useState(false);

  useKeypress('Escape', onClose);

  useEffect(() => {
    const checkOrientation = () => {
      setIsDeviceLandscape(window.innerWidth > window.innerHeight);
    };

    // Check initial orientation
    checkOrientation();

    // Add listener for orientation changes
    window.addEventListener('resize', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
    };
  }, []);

  useEffect(() => {
    visible && setMounted(true);
  }, [visible]);

  const onAnimationEnd = () => {
    if (!visible) {
      setMounted(false);
    }
  };

  if (!mounted) return null;

  // Use isDeviceLandscape to determine if we should apply rotation
  const shouldRotate = isLandscape && !isDeviceLandscape;

  return ReactDOM.createPortal(
    <>
      <Backdrop
        bgOpacity={bgOpacity}
        visible={visible}
        onClick={!isBackdropClose ? undefined : onClose}
        zIndex={zIndex}
      />
      <Content
        allowContentScroll={allowContentScroll}
        visible={visible}
        onAnimationEnd={onAnimationEnd}
        size={size}
        hasDropdowns={hasDropdowns}
        maxWidth={maxWidth}
        contentBorderRadius={contentBorderRadius}
        widthOverride={widthOverride}
        heightOverride={heightOverride}
        showVanityBorder={showVanityBorder}
        vanityBorderColor={vanityBorderColor}
        hasBackgroundColor={hasBackgroundColor}
        backgroundColor={backgroundColor}
        className="modal-content"
        isLandscape={shouldRotate}
        isDeviceLandscape={isDeviceLandscape}
      >
        <div className="modal-inner-content">
          <ModalHeaderContainer isLandscape={shouldRotate}>
            {showCloseButton && (
              <CloseButton
                top={closeBtnPos?.top}
                right={closeBtnPos?.right}
                onClick={onClose}
                isLandscape={shouldRotate}
              >
                <X color={theme.colors.darkGrey} />
              </CloseButton>
            )}
            {title && typeof title === 'object' && <>{title}</>}
            {title && typeof title === 'string' && (
              <ModalTitleContainer isLandscape={shouldRotate}>{title}</ModalTitleContainer>
            )}
          </ModalHeaderContainer>
          <ModalBodyContainer
            allowContentScroll={allowContentScroll}
            overrideStyles={overrideStyles}
            showScrollBar={showScrollBar}
            isLandscape={shouldRotate}
            isDeviceLandscape={isDeviceLandscape}
          >
            {children}
          </ModalBodyContainer>
        </div>
      </Content>
    </>,
    document.body
  );
};

export default Modal;
